import {useMemo, useRef} from 'react';
import type {ImageAdapter} from '@forecast-it/design-system';
import DirectApi from '../../../../../directApi';
import Util, {MAX_FILES_PER_UPLOAD, validateUploadFile} from '../../../util/util';
import {createToast} from '../../toasts/another-toast/toaster';
import CreateFileMutation from '../../../../../mutations/create_file_mutation';
import {fromFileReference, toFileReference} from './fileReference';

/**
 * The editor's own contract, aliased for readability at the call sites here.
 *
 * This was a local structural copy while the installed design-system predated
 * the type. Now that the dependency carries it, use the real one: a drift
 * between our adapter and what the editor actually calls should be a type error
 * rather than something that only shows up at runtime.
 */
export type InlineImageAdapter = ImageAdapter;

const reportRejected = (name: string, reason: string): void => {
	createToast({duration: 5000, message: `${name} could not be added: ${reason}`});
};

/**
 * Connects the editor's image plugin to task file storage.
 *
 * Uploads become ordinary files on the task, so they inherit its permissions and
 * cascade behaviour. What is written into the document is a reference, not the
 * serving URL — see fileReference for why — and the credential and privacy
 * attributes are applied here at render time because the storage format cannot
 * carry them.
 *
 * `getOwnFileIds` reports the files already on this task, and is a getter rather
 * than a snapshot on purpose — see `adoptReference` below.
 *
 * Exposed as a plain factory as well as a hook because every task-modal surface
 * that needs it is a class component. The modal builds one adapter and hands it
 * to all three; see `useInlineImageAdapter` below for the hook form.
 *
 * **Hold the returned object's identity stable for as long as `taskId` is
 * unchanged.** `RichTextEditor` rebuilds its plugin list when the adapter
 * changes, which re-deserializes the whole document — and it must never be
 * keyed on the file set, because `create_file_mutation` RANGE_ADDs onto
 * `Task_files`, so every successful inline upload would rebuild the editor at
 * the moment the user just inserted an image.
 */
export const createInlineImageAdapter = (taskId: string, getOwnFileIds: () => Set<string>): InlineImageAdapter => {
	const uploadBlob = (file: File): Promise<string | null> =>
		new Promise((resolve, reject) => {
			// The batch uploader normally enforces these, but inline insertion bypasses
			// it to get a file id back synchronously, so the same rule set is applied
			// here rather than duplicated.
			const rejection = validateUploadFile({name: file.name, mimeType: file.type, file});
			if (rejection) {
				reportRejected(file.name, rejection);
				resolve(null);
				return;
			}

			Util.CommitMutation(
				CreateFileMutation,
				// The File must NOT go in here. Relay JSON-serialises variables, so it
				// would arrive as {} and create a row with no bytes behind it.
				{taskId, name: file.name, mimeType: file.type, size: file.size, file: undefined},
				(response: {createFile?: {error?: string; file?: {node: {id: string}}}}) => {
					const created = response?.createFile;
					resolve(created?.error || !created?.file ? null : toFileReference(created.file.node.id));
				},
				true,
				// Uploadables is what actually transfers the bytes, via multipart.
				{file},
				(transaction: unknown, onFailure: (t: unknown) => boolean) => {
					// A transient or CORS failure schedules its own retry and says so via
					// the return value — leave the promise pending so the retry settles it.
					if (onFailure(transaction)) return;
					reject(new Error(`Inline image upload failed: ${file.name}`));
				}
			);
		});

	return {
		maxFilesPerAction: MAX_FILES_PER_UPLOAD,

		upload: uploadBlob,

		resolve: (reference: string) => {
			const fileId = fromFileReference(reference);
			return {
				src: fileId ? DirectApi.fileSrc(fileId) : '',
				// Sends cookies so the authenticated asset endpoint serves the image.
				crossOrigin: 'use-credentials' as const,
				// Keeps the src out of session-replay recordings, so the replay player
				// does not hammer the backend with unauthenticated requests.
				'data-private': true,
				loading: 'lazy' as const,
			};
		},

		/**
		 * Decides whether an incoming <img> src is ours, and what to store for it.
		 *
		 * Accepts our own reference scheme, and a resolved file_stream URL — which
		 * is what HTML copied out of a rendered description carries — normalising
		 * the latter back to a reference so the resolved form is never stored.
		 * Anything else is a third-party image and is rejected.
		 */
		recogniseSrc: (src: string) => {
			if (fromFileReference(src)) return src;

			const base = DirectApi.fileSrc('');
			if (src.startsWith(base)) {
				const fileId = src.slice(base.length).split(/[?#]/)[0];
				return fileId ? toFileReference(fileId) : null;
			}
			return null;
		},

		/**
		 * Makes an incoming reference belong to this task.
		 *
		 * Rich text copied from another task carries that task's file references.
		 * Left alone, the image would vanish when the original is removed, and may
		 * not even be readable by this task's viewers if the two sit in different
		 * projects. Re-uploading keeps every task's inline images self-contained;
		 * a reference that cannot be fetched is dropped rather than stored broken.
		 *
		 * The ids are read here, at call time, and never captured. Plate installs
		 * this override through `withOverrides` when the editor is *constructed*, so
		 * a later adapter's closure never replaces this one — a snapshot taken at
		 * construction would stay frozen for the editor's whole life. With one,
		 * copying an image inserted during this session and pasting it again in the
		 * same task would look foreign and be re-uploaded, duplicating a file the
		 * task already owns; and if `task.files` had not resolved when the editor
		 * mounted, every re-paste would duplicate.
		 */
		adoptReference: async (reference: string) => {
			const fileId = fromFileReference(reference);
			if (!fileId) return null;
			if (getOwnFileIds().has(fileId)) return reference;

			try {
				const response = await fetch(DirectApi.fileSrc(fileId), {credentials: 'include'});
				if (!response.ok) return null;
				const blob = await response.blob();
				return await uploadBlob(new File([blob], `image.${blob.type.split('/')[1] || 'png'}`, {type: blob.type}));
			} catch {
				return null;
			}
		},
	};
};

/**
 * Hook form, for function components.
 *
 * Memoised on `taskId` alone. The getter is reached through a ref so that a
 * caller passing an inline arrow — the natural call style — cannot change the
 * adapter's identity, which is the one thing that must not happen: see the
 * factory's note on why keying an adapter on the file set rebuilds the editor
 * mid-edit without even refreshing adoption.
 */
export const useInlineImageAdapter = (taskId: string, getOwnFileIds: () => Set<string>): InlineImageAdapter => {
	const getOwnFileIdsRef = useRef(getOwnFileIds);
	getOwnFileIdsRef.current = getOwnFileIds;

	return useMemo(() => createInlineImageAdapter(taskId, () => getOwnFileIdsRef.current()), [taskId]);
};
